import {getAudioContext} from "../audioContext";
import {logger} from "../log/logger";

/**
 * InstrumentSamplePlayer represents a single audio sample to be played in an instrument-like fashion.
 * It is meant to have play called over and over again, as needed.
 * This class is responsible for:
 * - making a fetch/ajax call to retrieve audio files, such as mp3, wav, etc
 * - caching the retrieved audioBuffer
 * - playing the audio sample.
 * - providing a release envelope used to control how fast we want releases to occur (initial recording could have a 15 second release, but we want a 1 second release)
 *
 * https://padenot.github.io/web-audio-perf/
 */
export default class InstrumentSamplePlayer {
  //url of the audio resource that is to be loaded/decoded/played.
  url: string;
  //The R of ASR envelope.  Used when release() is called to gradually reduce the volume to 0, over this many seconds.
  releaseTimeSeconds: number;
  //cached decoded audio data retrieved from the url.
  audioBuffer: AudioBuffer;
  //what our source node should be connected to before being played. (reverb, filter, etc)
  destination: AudioNode;
  //gain node used for Release envelope.
  noteGainNode: GainNode;
  //when we schedule notes to be played in the future, we need the ability to later cancel/stop those scheduled notes, so we need to hold onto all the scheduled sources. e.g. when the user clicks stop.
  scheduledPlaySources: AudioBufferSourceNode[] = [];
  //we don't want the same sample playing twice and overlapping, so hold on to the source so we can call stop.
  source: AudioBufferSourceNode;

  protected createConnectedGainNode = createConnectedGainNode;
  protected createAudioSourceFromAudioBuffer = createAudioSourceFromAudioBuffer;
  protected ensureAudioBufferHasBeenLoaded = ensureAudioBufferHasBeenLoaded;

  /**
   *
   * @param url - url of the audio resource file (mp3, wav, etc)
   * @param context - audio context used for creating gain nodes, audio source nodes, etc.
   * @param destination - the AudioDestinationNode our web audio node should be connected to. (could be filter node, reverb node, context.destination, etc)
   * @param releaseTimeSeconds - when release() is called, we linearly decrease the volume over this many seconds, so that the volume is 0 after this many seconds has passed.
   */
  constructor({url, destination, releaseTimeSeconds=1,}: {url: string; destination: AudioNode; releaseTimeSeconds?: number}) {
    Object.assign(this, {url, destination, releaseTimeSeconds,});
  }

  /**
   * Since it's not a good idea to make ajax requests in a constructor
   * (they can't be marked async, and you can't return a promise from them, so noone on the outside can know when async ops have completed),
   * load() must be called separately before play can be called.
   */
  async load(){
    this.audioBuffer  = await fetchAndDecodeAudioData({url: this.url, context: getAudioContext()});
  }

  /**
   * Plays the audio sample.
   * Load must be called prior to play being called, or an exception will be thrown.
   * Eventually we may end up fully implementing a ASR envelope (attack, sustain, release), but for now we just need release.
   * Note: we do not attempt to share a gain node for each playing of the note, as calling this.noteGainNode.gain.cancelScheduledValues() can lead to pops and clicks in playback.
   * technically/realistically you can't play the same note on the piano twice, and this code allows you to do that, but so far my experiments with cancelScheduledValues has resulted in glitchy audio.
   * @param volume
   */
  play({volume = 1,}: {volume?: number} ){

    const context = getAudioContext();
    this.stopImmediately();//don't allow the sample to be played twice (overlapping)
    ensureAudioBufferHasBeenLoaded(this.audioBuffer); //throw an exception if load() hasn't been completed.
    //create the gain we will use as a release envelope.  (when the note is released, we want to ramp down the gain volume over N time)
    this.noteGainNode = createConnectedGainNode({destination: this.destination, context, volume});
    //create a new audio source, as once start has been called, it cannot be called again.
    const source = createAudioSourceFromAudioBuffer(this.audioBuffer, context);
    //connect to our gain, which should be connected to our destination (passed in by Synthesizer)
    source.connect(this.noteGainNode);
    this.source = source;
    // pass start at 0 to force starting when called.
    // without starting at 0 it doesn't seem to always start right away
    source.start(0);
  }

  stopImmediately(){
    if(this.source){
      // this.source.stop(); <- this will result in pops & clicks due to the sample being stopped abruptly
      this.noteGainNode.gain.setTargetAtTime(0, getAudioContext().currentTime ,  0.1);
    }
  }

  /**
   * Lower the volume of the audio sample over N seconds (this.releaseTimeSeconds) to give the effect of the note trailing off after it has been plucked/released on the keyboard.
   */
  release(){
    if(!this.noteGainNode){
      logger.lessonAudio.error({message: `no gain` + this.url});
    }
    this.noteGainNode.gain.setTargetAtTime(0, getAudioContext().currentTime , this.releaseTimeSeconds / 3);  // no pops in chrome.
  }

  schedulePlay({volume = 1, startAtContextRelativeSeconds, durationInSeconds, context=getAudioContext(), destination=this.destination}: {volume?: number; startAtContextRelativeSeconds: number; durationInSeconds: number, context?: AudioContext | OfflineAudioContext, destination?: AudioNode} ){
    //create the gain we will use as a release envelope.  (when the note is released, we want to ramp down the gain volume over N time)
    const gainNode = createConnectedGainNode({destination, context, volume});
    //create a new audio source, as once start has been called, it cannot be called again.
    const source = createAudioSourceFromAudioBuffer(this.audioBuffer, context);
    //connect to our gain, which should be connected to our destination (passed in by Synthesizer)
    source.connect(gainNode);
    source.start(startAtContextRelativeSeconds);
    gainNode.gain.setTargetAtTime(0, startAtContextRelativeSeconds + durationInSeconds , this.releaseTimeSeconds / 3);  // no pops in chrome.
    this.scheduledPlaySources.push(source);
  }

  stopAllScheduledPlays(){
    //iterate over each source and _try_ to stop it.  (we don't know if start has been called or not, and calling stop before start throws an exception)
    this.scheduledPlaySources.forEach(s => {
      try{
        s.stop();
      }catch(e){}
    });
    this.scheduledPlaySources = [];
  }
}

/**
 * Convenience function to create a gain node that is connected to a destination, has volume assigned, etc.
 * @param destination - node that the gain node should be connected to
 * @param context - audio context used to create the gain node.
 * @param volume - volume of the gain node.
 */
function createConnectedGainNode({destination, context, volume =1, }: {context: AudioContext | OfflineAudioContext; destination: AudioNode; volume?: number}){
  const gainNode = context.createGain();
  gainNode.connect(destination);
  gainNode.gain.value = volume;
  return gainNode;
}

/**
 * Retrieves, decodes, and returns an audio data array buffer of the audio data found at the specified url.
 * @param url - url of mp3, wav, etc.
 * @param context - audio context used to decodeAudioData
 */
async function fetchAndDecodeAudioData({url, context}: {url: string; context: AudioContext}): Promise<AudioBuffer> {
  const response = await fetch(url, {cache: "force-cache"});
  const arrayBuffer = await response.arrayBuffer();
  return decodeAudioData(context, arrayBuffer);
}

/**
 * Safari doesn't support the promise based decodeAudioData func, so provide our own.
 * https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/decodeAudioData
 * @param context
 * @param arrayBuffer
 */
async function decodeAudioData(context: AudioContext, arrayBuffer: ArrayBuffer): Promise<AudioBuffer>{
  return new Promise((resolve, reject) => context.decodeAudioData(arrayBuffer, resolve, reject));
}

/**
 * Convenience function to create an AudioBufferSourceNode, which is used to play/stop/etc an audio sample.
 * @param audioBuffer - decoded audio data representation of the audio sample
 * @param context - audio context used to create the AudioBufferSourceNode
 */
function createAudioSourceFromAudioBuffer(audioBuffer: AudioBuffer, context: AudioContext | OfflineAudioContext): AudioBufferSourceNode {
  const source = context.createBufferSource();
  source.buffer = audioBuffer;
  return source;
}

/**
 * Convenience function for throwing an exception when audioBuffer data is undefined.
 * @param audioBuffer
 */
function ensureAudioBufferHasBeenLoaded(audioBuffer: AudioBuffer){
  if(!audioBuffer){ throw new Error('InstrumentSamplePlayer requires the load() function be called before trying to play audio sample. '); }
}
